import { cn } from "@/lib/utils";
import { Shield, AlertTriangle, AlertCircle, BadgeCheck } from "lucide-react";

type BadgeStatus = "safe" | "caution" | "risk" | "verified";

interface StatusBadgeProps {
  status: BadgeStatus;
  size?: "sm" | "md" | "lg";
  showLabel?: boolean;
  className?: string;
}

const statusConfig = {
  safe: {
    icon: Shield,
    label: "Established Website",
    gradient: "gradient-safe",
    bgClass: "bg-safe-bg",
    textClass: "text-safe",
    borderClass: "border-safe/30",
  },
  caution: {
    icon: AlertTriangle,
    label: "New / Unknown",
    gradient: "gradient-caution",
    bgClass: "bg-caution-bg",
    textClass: "text-caution",
    borderClass: "border-caution/30",
  },
  risk: {
    icon: AlertCircle,
    label: "Risky / Potential Scam",
    gradient: "gradient-risk",
    bgClass: "bg-risk-bg",
    textClass: "text-risk",
    borderClass: "border-risk/30",
  },
  verified: {
    icon: BadgeCheck,
    label: "Verified by Transparency Badge",
    gradient: "gradient-hero",
    bgClass: "bg-accent",
    textClass: "text-primary",
    borderClass: "border-primary/30",
  },
};

const sizeConfig = {
  sm: {
    container: "h-10 w-10",
    icon: "h-5 w-5",
    labelText: "text-xs",
  },
  md: {
    container: "h-14 w-14",
    icon: "h-7 w-7",
    labelText: "text-sm",
  },
  lg: {
    container: "h-20 w-20",
    icon: "h-10 w-10",
    labelText: "text-base",
  },
};

export function StatusBadge({ status, size = "md", showLabel = true, className }: StatusBadgeProps) {
  const config = statusConfig[status];
  const sizeClasses = sizeConfig[size];
  const Icon = config.icon;

  return (
    <div className={cn("flex flex-col items-center gap-2", className)}>
      <div
        className={cn(
          "rounded-full flex items-center justify-center shadow-badge transition-transform hover:scale-105",
          config.gradient,
          sizeClasses.container
        )}
      >
        <Icon className={cn("text-white", sizeClasses.icon)} />
      </div>
      {showLabel && (
        <span className={cn("font-medium", config.textClass, sizeClasses.labelText)}>
          {config.label}
        </span>
      )}
    </div>
  );
}

export function StatusBadgeCard({ status, description }: { status: BadgeStatus; description: string }) {
  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <div
      className={cn(
        "rounded-xl border p-6 transition-all hover:shadow-elevated h-full",
        config.bgClass,
        config.borderClass
      )}
    >
      <div className="flex items-start gap-4">
        <div className={cn("rounded-full p-3", config.gradient)}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <div>
          <h3 className={cn("font-semibold mb-1", config.textClass)}>{config.label}</h3>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
    </div>
  );
}
